// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 zouwei

/**
 * Scene → SVG renderer. Editing and export are isomorphic: the same
 * per-element markup is used live in a host and in the exported
 * `.mboard.svg`, so what you draw is what you export (spec §4.2).
 *
 * Output is a string of SVG fragments grouped by element type
 * (`<g class="mboard-strokes|shapes|text|formulas|images">`). Hosts may
 * either inject this string or mirror the same attribute logic in their own
 * reactive DOM; keeping it as pure functions makes both paths share one
 * source of truth.
 */

import { getStroke } from 'perfect-freehand'
import { linearPoints } from './geometry'
import { renderFormulaHtml } from './formula'
import { frameClipDefs, frameClipMap } from './frame'
import { sceneMetadataBlock } from './payload'
import { roughShapeSvg } from './rough-shape'
import type {
  Arrowhead,
  AudioElement,
  BoardElement,
  BoardScene,
  CustomElement,
  FormulaElement,
  FrameElement,
  ImageElement,
  ShapeElement,
  StrokeElement,
  TextElement,
  VideoElement,
} from './types'

/**
 * A host-supplied renderer for one custom-node `kind`, producing the SVG body
 * for that element in a static `.mboard.svg` export. The host owns the safety
 * of the returned markup (escape/sanitize any user-derived text). When no
 * renderer is registered for a kind, the core draws a neutral placeholder.
 */
export type CustomRenderer = (el: CustomElement) => string
/** Map of custom-node `kind` → renderer, keyed by the host-namespaced string. */
export type CustomRenderers = Record<string, CustomRenderer>

/** Escape text content / attribute values for safe inclusion in SVG/XML. */
export function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

function opacityAttr(el: BoardElement): string {
  return el.opacity !== undefined && el.opacity !== 1
    ? ` opacity="${el.opacity}"`
    : ''
}

function transformAttr(el: BoardElement, cx: number, cy: number): string {
  const parts: string[] = []
  if (el.rotation) parts.push(`rotate(${(el.rotation * 180) / Math.PI} ${cx} ${cy})`)
  if (el.flipX || el.flipY) {
    // Mirror around the bbox center: translate → scale(±1) → translate back.
    const sx = el.flipX ? -1 : 1
    const sy = el.flipY ? -1 : 1
    parts.push(`translate(${round(cx)} ${round(cy)}) scale(${sx} ${sy}) translate(${round(-cx)} ${round(-cy)})`)
  }
  return parts.length ? ` transform="${parts.join(' ')}"` : ''
}

const round = (n: number): number => Math.round(n * 100) / 100

/** perfect-freehand outline points → a closed SVG path `d`. */
export function strokeOutlineToPath(outline: number[][]): string {
  if (outline.length === 0) return ''
  const d = outline.reduce(
    (acc, [x0, y0], i, arr) => {
      const [x1, y1] = arr[(i + 1) % arr.length]!
      acc.push(round(x0!), round(y0!), round((x0! + x1!) / 2), round((y0! + y1!) / 2))
      return acc
    },
    ['M', round(outline[0]![0]!), round(outline[0]![1]!), 'Q'] as (string | number)[],
  )
  d.push('Z')
  return d.join(' ')
}

function renderStroke(el: StrokeElement): string {
  const outline = getStroke(
    el.points.map((p) => [p[0], p[1], p[2]]),
    { size: el.size, thinning: 0.5, smoothing: 0.5, streamline: 0.5 },
  )
  const path = strokeOutlineToPath(outline)
  const color = el.color ?? '#1a1a1a'
  return `<path d="${path}" fill="${color}" transform="translate(${round(el.x)} ${round(el.y)})"${opacityAttr(el)}/>`
}

/** stroke-dasharray for a dashed/dotted stroke style (scaled by width). */
function dashAttr(el: ShapeElement): string {
  const w = el.strokeWidth
  if (el.strokeStyle === 'dashed') return ` stroke-dasharray="${w * 3} ${w * 3}"`
  if (el.strokeStyle === 'dotted') return ` stroke-dasharray="${w * 0.5} ${w * 2.5}" stroke-linecap="round"`
  return ''
}

/** Arrowhead SVG at `(px,py)` pointing along angle `a` (radians). */
function arrowheadSvg(kind: Arrowhead, px: number, py: number, a: number, size: number, color: string): string {
  if (kind === 'none') return ''
  const c = Math.cos(a)
  const s = Math.sin(a)
  const pt = (dist: number, perp: number) => `${round(px - c * dist - s * perp)} ${round(py - s * dist + c * perp)}`
  switch (kind) {
    case 'arrow':
      return `<path d="M${pt(size, size * 0.6)} L${round(px)} ${round(py)} L${pt(size, -size * 0.6)}" fill="none" stroke="${color}" stroke-width="${round(size * 0.28)}" stroke-linecap="round" stroke-linejoin="round"/>`
    case 'triangle':
      return `<path d="M${round(px)} ${round(py)} L${pt(size, size * 0.55)} L${pt(size, -size * 0.55)} Z" fill="${color}" stroke="none"/>`
    case 'bar':
      return `<line x1="${pt(0, size * 0.7)}" x2="${pt(0, -size * 0.7)}" stroke="${color}" stroke-width="${round(size * 0.28)}" stroke-linecap="round"/>`
    case 'dot':
      return `<circle cx="${round(px)}" cy="${round(py)}" r="${round(size * 0.4)}" fill="${color}"/>`
    default:
      return ''
  }
}

/**
 * Catmull-Rom spline through `pts` → smooth cubic-bezier path `d`. Used when a
 * multi-point line/arrow has `edges: 'round'` (curved routing). Endpoints are
 * clamped so the curve passes through every vertex.
 */
function smoothPath(pts: { x: number; y: number }[]): string {
  const n = pts.length
  let d = `M${round(pts[0]!.x)} ${round(pts[0]!.y)}`
  for (let i = 0; i < n - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i]!
    const p1 = pts[i]!
    const p2 = pts[i + 1]!
    const p3 = pts[i + 2] ?? p2
    const c1x = p1.x + (p2.x - p0.x) / 6
    const c1y = p1.y + (p2.y - p0.y) / 6
    const c2x = p2.x - (p3.x - p1.x) / 6
    const c2y = p2.y - (p3.y - p1.y) / 6
    d += `C${round(c1x)} ${round(c1y)} ${round(c2x)} ${round(c2y)} ${round(p2.x)} ${round(p2.y)}`
  }
  return d
}

function renderShape(el: ShapeElement): string {
  const stroke = el.color ?? '#1a1a1a'
  const fill = el.fill ?? 'none'
  const cx = el.x + el.width / 2
  const cy = el.y + el.height / 2
  const rot = transformAttr(el, cx, cy)
  const dash = dashAttr(el)

  // Linear elements: line / arrow (+ optional per-end arrowheads).
  if (el.kind === 'line' || el.kind === 'arrow') {
    const pts = linearPoints(el)
    const n = pts.length
    const p0 = pts[0]!
    const pEnd = pts[n - 1]!
    // Arrowhead angles come from the terminal segments (matches two-point case).
    const aEnd = Math.atan2(pEnd.y - pts[n - 2]!.y, pEnd.x - pts[n - 2]!.x)
    const aStart = Math.atan2(p0.y - pts[1]!.y, p0.x - pts[1]!.x)
    const hSize = Math.max(9, el.strokeWidth * 4)
    let body: string
    if (el.sloppiness) {
      body = roughShapeSvg(el, dash)
    } else if (n > 2) {
      const d =
        el.edges === 'round'
          ? smoothPath(pts)
          : pts.map((p, i) => `${i ? 'L' : 'M'}${round(p.x)} ${round(p.y)}`).join(' ')
      body = `<path d="${d}" fill="none" stroke="${stroke}" stroke-width="${el.strokeWidth}" stroke-linejoin="round" stroke-linecap="round"${dash}/>`
    } else {
      // Two-point line keeps the legacy default arrow marker when no arrowheads
      // are specified (backward compatible).
      const legacyMarker =
        el.kind === 'arrow' && el.endArrowhead === undefined && el.startArrowhead === undefined
          ? ' marker-end="url(#mboard-arrow)"'
          : ''
      body = `<line x1="${el.x}" y1="${el.y}" x2="${round(pEnd.x)}" y2="${round(pEnd.y)}" stroke="${stroke}" stroke-width="${el.strokeWidth}"${dash}${legacyMarker}/>`
    }
    let heads = ''
    // n>2 has no legacy marker, so an arrow needs an explicit head drawn.
    if (el.endArrowhead !== undefined || el.startArrowhead !== undefined || (n > 2 && el.kind === 'arrow')) {
      const end = el.endArrowhead ?? (el.kind === 'arrow' ? 'arrow' : 'none')
      const start = el.startArrowhead ?? 'none'
      heads =
        arrowheadSvg(end, pEnd.x, pEnd.y, aEnd, hSize, stroke) +
        arrowheadSvg(start, p0.x, p0.y, aStart, hSize, stroke)
    }
    return `<g${rot}${opacityAttr(el)}>${body}${heads}</g>`
  }

  // Closed shapes: rough (sloppiness>0) or clean primitives.
  if (el.sloppiness) {
    return `<g${rot}${opacityAttr(el)}>${roughShapeSvg(el, dash)}</g>`
  }
  const common = `stroke="${stroke}" stroke-width="${el.strokeWidth}" fill="${fill}"${dash}`
  const rx = el.edges === 'round' ? Math.min(Math.abs(el.width), Math.abs(el.height)) * 0.18 : 2
  switch (el.kind) {
    case 'rect':
      return `<rect x="${el.x}" y="${el.y}" width="${el.width}" height="${el.height}" rx="${rx}" ${common}${rot}${opacityAttr(el)}/>`
    case 'ellipse':
      return `<ellipse cx="${cx}" cy="${cy}" rx="${el.width / 2}" ry="${el.height / 2}" ${common}${rot}${opacityAttr(el)}/>`
    case 'diamond':
      return `<polygon points="${cx},${el.y} ${el.x + el.width},${cy} ${cx},${el.y + el.height} ${el.x},${cy}" stroke-linejoin="round" ${common}${rot}${opacityAttr(el)}/>`
  }
}

const DEFAULT_TEXT_FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"

function renderText(el: TextElement): string {
  const color = el.color ?? '#1a1a1a'
  const family = el.fontFamily ?? DEFAULT_TEXT_FONT
  const lines = el.text.split('\n')
  const lineHeight = el.fontSize * 1.3
  const w = el.measured?.width ?? 0
  const h = el.measured?.height ?? lines.length * lineHeight
  const rot = transformAttr(el, el.x + w / 2, el.y + h / 2)
  // Align each line within the measured width via text-anchor + anchor x.
  const align = el.textAlign ?? 'left'
  const anchor = align === 'center' ? 'middle' : align === 'right' ? 'end' : 'start'
  const anchorX = el.x + (align === 'center' ? w / 2 : align === 'right' ? w : 0)
  const tspans = lines
    .map(
      (line, i) =>
        `<tspan x="${anchorX}" dy="${i === 0 ? el.fontSize : lineHeight}">${escapeXml(line) || ' '}</tspan>`,
    )
    .join('')
  return `<text y="${el.y}" font-size="${el.fontSize}" fill="${color}" font-family="${escapeXml(family)}" text-anchor="${anchor}"${rot}${opacityAttr(el)}>${tspans}</text>`
}

function renderFormula(el: FormulaElement): string {
  const w = el.measured?.width ?? el.fontSize * Math.max(1, el.latex.length) * 0.6
  const h = el.measured?.height ?? el.fontSize * 1.6
  const rot = transformAttr(el, el.x + w / 2, el.y + h / 2)
  const html = renderFormulaHtml(el.latex, { display: el.display })
  // KaTeX glyphs inherit `color`; set it on the wrapper so a formula's color
  // is honored (parity with text/stroke/shape). Fallback <text> uses `fill`.
  const color = el.color ?? '#1a1a1a'
  // foreignObject carries KaTeX's compiled HTML (from LaTeX source we
  // control — not raw user HTML). A <switch> fallback shows the LaTeX source
  // if a viewer (e.g. GitHub's <img> sanitizer) drops foreignObject.
  return (
    `<g class="mboard-formula" transform="translate(${round(el.x)} ${round(el.y)})"${rot}${opacityAttr(el)}>` +
    `<switch>` +
    `<foreignObject width="${round(w)}" height="${round(h)}" requiredExtensions="http://www.w3.org/1999/xhtml">` +
    `<div xmlns="http://www.w3.org/1999/xhtml" style="font-size:${el.fontSize}px;color:${color}">${html}</div>` +
    `</foreignObject>` +
    `<text y="${el.fontSize}" font-size="${el.fontSize}" fill="${color}" font-family="serif">${escapeXml(el.latex)}</text>` +
    `</switch></g>`
  )
}

function renderImage(el: ImageElement): string {
  const rot = transformAttr(el, el.x + el.width / 2, el.y + el.height / 2)
  const alt = el.alt ? ` aria-label="${escapeXml(el.alt)}"` : ''
  const c = el.crop
  if (c && c.width > 0 && c.height > 0) {
    // Draw the full source scaled so the crop region maps onto (x,y,w,h), then
    // clip to (x,y,w,h). preserveAspectRatio="none" lets the crop fill the box.
    const fullW = el.width / c.width
    const fullH = el.height / c.height
    const imgX = el.x - c.x * fullW
    const imgY = el.y - c.y * fullH
    const clipId = `mboard-crop-${el.id}`
    return (
      `<g${alt}${rot}${opacityAttr(el)}>` +
      `<clipPath id="${clipId}"><rect x="${el.x}" y="${el.y}" width="${el.width}" height="${el.height}"/></clipPath>` +
      `<image href="${escapeXml(el.src)}" x="${round(imgX)}" y="${round(imgY)}" width="${round(fullW)}" height="${round(fullH)}" preserveAspectRatio="none" clip-path="url(#${clipId})"/>` +
      `</g>`
    )
  }
  return `<image href="${escapeXml(el.src)}" x="${el.x}" y="${el.y}" width="${el.width}" height="${el.height}"${alt}${rot}${opacityAttr(el)}/>`
}

/** Centered "play" badge (dark disc + white triangle) for the static video fallback. */
function playBadge(cx: number, cy: number, box: number): string {
  const r = Math.max(8, box * 0.12)
  const t = r * 0.55
  const disc = `<circle cx="${round(cx)}" cy="${round(cy)}" r="${round(r)}" fill="rgba(0,0,0,0.55)"/>`
  const tri = `<path d="M${round(cx - t * 0.55)} ${round(cy - t)} L${round(cx + t)} ${round(cy)} L${round(cx - t * 0.55)} ${round(cy + t)} Z" fill="#fff"/>`
  return disc + tri
}

/**
 * Video: a live interactive <video> inside <foreignObject>, wrapped in a
 * <switch> whose fallback (poster image + play badge) is the ONLY thing that
 * survives export. A statically-viewed .mboard.svg, a rasterized PNG (rasterize
 * paints the SVG via <img>, where <video> is blank), and foreignObject-stripping
 * sanitizers all fall through to that fallback. So the live element is
 * edit-time only; the poster is what a shared/exported board actually shows.
 * HLS `src` (.m3u8) plays only where the host attaches an HLS loader — the core
 * emits the plain <video>; format handling is the host's job.
 */
function renderVideo(el: VideoElement): string {
  const rot = transformAttr(el, el.x + el.width / 2, el.y + el.height / 2)
  const src = safeUrl(el.src)
  const poster = safeUrl(el.poster)
  const attrs =
    (src ? ` src="${escapeXml(src)}"` : '') +
    ' controls preload="metadata"' +
    (el.loop ? ' loop' : '') +
    (el.muted ? ' muted' : '') +
    (el.autoplay ? ' autoplay' : '') +
    (poster ? ` poster="${escapeXml(poster)}"` : '')
  const label = el.alt ? ` aria-label="${escapeXml(el.alt)}"` : ''
  const bg = `<rect x="${el.x}" y="${el.y}" width="${el.width}" height="${el.height}" fill="#000" rx="4"/>`
  const posterImg = poster
    ? `<image href="${escapeXml(poster)}" x="${el.x}" y="${el.y}" width="${el.width}" height="${el.height}" preserveAspectRatio="xMidYMid meet"/>`
    : ''
  const fallback = `<g>${bg}${posterImg}${playBadge(el.x + el.width / 2, el.y + el.height / 2, Math.min(el.width, el.height))}</g>`
  return (
    `<g class="mboard-video"${label}${rot}${opacityAttr(el)}>` +
    `<switch>` +
    `<foreignObject x="${el.x}" y="${el.y}" width="${el.width}" height="${el.height}" requiredExtensions="http://www.w3.org/1999/xhtml">` +
    `<video xmlns="http://www.w3.org/1999/xhtml" style="width:100%;height:100%;object-fit:contain;background:#000"${attrs}></video>` +
    `</foreignObject>` +
    fallback +
    `</switch></g>`
  )
}

/**
 * Audio: a live <audio> inside <foreignObject>, with a static cover card
 * fallback (background + cover image + title) for exported/rasterized/sanitized
 * contexts, mirroring renderVideo.
 */
function renderAudio(el: AudioElement): string {
  const rot = transformAttr(el, el.x + el.width / 2, el.y + el.height / 2)
  const src = safeUrl(el.src)
  const poster = safeUrl(el.poster)
  const attrs =
    (src ? ` src="${escapeXml(src)}"` : '') +
    ' controls preload="metadata"' +
    (el.loop ? ' loop' : '')
  const label = el.title ? ` aria-label="${escapeXml(el.title)}"` : ''
  const pad = Math.min(el.height * 0.15, 12)
  const cover = Math.max(0, el.height - pad * 2)
  const coverEl = poster
    ? `<image href="${escapeXml(poster)}" x="${round(el.x + pad)}" y="${round(el.y + pad)}" width="${round(cover)}" height="${round(cover)}" preserveAspectRatio="xMidYMid slice"/>`
    : `<rect x="${round(el.x + pad)}" y="${round(el.y + pad)}" width="${round(cover)}" height="${round(cover)}" fill="#e4e4e7" rx="4"/>`
  const fontSize = round(Math.min(el.height * 0.26, 15))
  const titleX = round(el.x + pad * 2 + cover)
  const titleY = round(el.y + el.height / 2)
  const titleText = el.title ? escapeXml(el.title) : '♪ audio'
  const titleFill = el.title ? '#18181b' : '#71717a'
  const card =
    `<g>` +
    `<rect x="${el.x}" y="${el.y}" width="${el.width}" height="${el.height}" fill="#f4f4f5" stroke="#d4d4d8" rx="6"/>` +
    coverEl +
    `<text x="${titleX}" y="${titleY}" font-size="${fontSize}" fill="${titleFill}" dominant-baseline="middle">${titleText}</text>` +
    `</g>`
  return (
    `<g class="mboard-audio"${label}${rot}${opacityAttr(el)}>` +
    `<switch>` +
    `<foreignObject x="${el.x}" y="${el.y}" width="${el.width}" height="${el.height}" requiredExtensions="http://www.w3.org/1999/xhtml">` +
    `<audio xmlns="http://www.w3.org/1999/xhtml" style="width:100%"${attrs}></audio>` +
    `</foreignObject>` +
    card +
    `</switch></g>`
  )
}

/** Render a single element to an SVG fragment string. */
/**
 * Allow only safe hyperlink schemes. Rejects javascript:/vbscript:/data: and
 * anything else that could execute on click — defense-in-depth for untrusted
 * `.boardraw`/SVG files, independent of any host-side sanitization.
 */
export function safeUrl(url: string | undefined): string | null {
  if (!url) return null
  const trimmed = url.trim()
  // Relative URLs and fragments are safe.
  if (/^(#|\/|\.\/|\.\.\/)/.test(trimmed)) return trimmed
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(trimmed)?.[1]
  if (!scheme) return trimmed // schemeless (e.g. "example.com/path")
  return /^(https?|mailto|tel)$/i.test(scheme) ? trimmed : null
}

function renderFrame(el: FrameElement): string {
  const rot = transformAttr(el, el.x + el.width / 2, el.y + el.height / 2)
  const name = el.name ?? 'Frame'
  const stroke = el.color ?? '#adb5bd'
  return (
    `<g class="mboard-frame"${rot}${opacityAttr(el)}>` +
    `<rect x="${el.x}" y="${el.y}" width="${el.width}" height="${el.height}" fill="none" stroke="${stroke}" stroke-width="1.5" rx="6"/>` +
    `<text x="${round(el.x + 2)}" y="${round(el.y - 6)}" font-size="13" fill="${stroke}" font-family="${DEFAULT_TEXT_FONT}">${escapeXml(name)}</text>` +
    `</g>`
  )
}

/**
 * Custom node → SVG. If the host registered a renderer for this `kind`, defer
 * to it (the host owns escaping); otherwise draw a neutral dashed placeholder
 * so unknown custom nodes stay visible and the `.mboard.svg` renders in any
 * viewer without host cooperation.
 */
function renderCustom(el: CustomElement, renderers?: CustomRenderers): string {
  const host = renderers?.[el.kind]
  if (host) return host(el)
  const rot = transformAttr(el, el.x + el.width / 2, el.y + el.height / 2)
  return (
    `<g class="mboard-custom" data-kind="${escapeXml(el.kind)}"${rot}${opacityAttr(el)}>` +
    `<rect x="${el.x}" y="${el.y}" width="${el.width}" height="${el.height}" fill="none" stroke="#adb5bd" stroke-width="1" stroke-dasharray="4 4" rx="4"/>` +
    `<text x="${round(el.x + 4)}" y="${round(el.y + 14)}" font-size="11" fill="#adb5bd" font-family="${DEFAULT_TEXT_FONT}">${escapeXml(el.kind)}</text>` +
    `</g>`
  )
}

function renderShapeOf(el: BoardElement, renderers?: CustomRenderers): string {
  switch (el.type) {
    case 'stroke':
      return renderStroke(el)
    case 'shape':
      return renderShape(el)
    case 'text':
      return renderText(el)
    case 'formula':
      return renderFormula(el)
    case 'image':
      return renderImage(el)
    case 'video':
      return renderVideo(el)
    case 'audio':
      return renderAudio(el)
    case 'frame':
      return renderFrame(el)
    case 'custom':
      return renderCustom(el, renderers)
  }
}

export function renderElement(el: BoardElement, renderers?: CustomRenderers): string {
  const body = renderShapeOf(el, renderers)
  const href = safeUrl(el.link)
  return href
    ? `<a href="${escapeXml(href)}" target="_blank" rel="noopener noreferrer">${body}</a>`
    : body
}

const GROUP: Record<BoardElement['type'], string> = {
  frame: 'mboard-frames',
  stroke: 'mboard-strokes',
  shape: 'mboard-shapes',
  text: 'mboard-text',
  formula: 'mboard-formulas',
  image: 'mboard-images',
  video: 'mboard-videos',
  audio: 'mboard-audios',
  custom: 'mboard-custom',
}

/**
 * Paint order: media (image/video/audio) at the bottom, then strokes/shapes/
 * text/formulas on top, then z, then array order. Media sits above image so an
 * annotated media clip keeps its ink/labels drawn over the frame.
 */
const LAYER_ORDER: BoardElement['type'][] = [
  'frame',
  'image',
  'video',
  'audio',
  'stroke',
  'shape',
  'text',
  'formula',
  // Custom domain overlays paint on top so host annotations sit over the ink.
  'custom',
]

const ARROW_MARKER =
  '<defs><marker id="mboard-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0 0 L10 5 L0 10 z" fill="context-stroke"/></marker></defs>'

export interface RenderSceneOptions {
  /** Embed the editable scene JSON in <metadata> (default true). */
  embedScene?: boolean
  /**
   * Per-kind renderers for `custom` nodes. A kind without a renderer falls
   * back to the neutral placeholder. Absent = all custom nodes use the
   * placeholder.
   */
  customRenderers?: CustomRenderers
}

/** Render a full scene to `.mboard.svg` text. */
export function renderSceneToSvg(
  scene: BoardScene,
  options: RenderSceneOptions = {},
): string {
  const { width, height } = scene.appState
  const bg = scene.appState.background
    ? `<rect width="${width}" height="${height}" fill="${scene.appState.background}"/>`
    : ''

  const byType = new Map<BoardElement['type'], BoardElement[]>()
  for (const el of scene.elements) {
    const list = byType.get(el.type) ?? []
    list.push(el)
    byType.set(el.type, list)
  }

  // Frame content is clipped to its frame's bounds.
  const clipMap = frameClipMap(scene)
  const clipDefs = frameClipDefs(scene)
  const paint = (el: BoardElement) => {
    const body = renderElement(el, options.customRenderers)
    const fid = clipMap.get(el.id)
    return fid ? `<g clip-path="url(#mboard-fclip-${fid})">${body}</g>` : body
  }

  const groups = LAYER_ORDER.map((type) => {
    const list = byType.get(type)
    if (!list || list.length === 0) return ''
    const sorted = [...list].sort((a, b) => (a.z ?? 0) - (b.z ?? 0))
    return `<g class="${GROUP[type]}">${sorted.map(paint).join('')}</g>`
  }).join('')

  const metadata = options.embedScene === false ? '' : sceneMetadataBlock(scene)

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">` +
    metadata +
    ARROW_MARKER +
    clipDefs +
    bg +
    groups +
    `</svg>`
  )
}
